import React, { useCallback, useMemo, useContext, createContext } from 'react';
import Loadable from 'react-loadable';
import { useDispatch } from 'react-redux';
import { Translations } from '@pegasus/translation';
import { useCountrySelector as useCountrySelectorShared } from '@pegasus/country-selector';

import { get, put } from 'src/client/api/services/countrySelector';
import { useConfig } from 'src/client/hoc/config';
import { isCookieEnabled, addSuccessFlashMessage, removeFlashMessage } from 'src/client/helpers';

type CountrySelector = ReturnType<typeof useCountrySelectorShared>;

const defaultCountryCodeContext = {
    isShown: false,
    country: null,
    onSubmit: () => Promise.resolve(),
};

const countryCodeContext = createContext<CountrySelector>(defaultCountryCodeContext);

export const useIsCountrySelectorEnabled = () => {
    const config = useConfig();
    const isEnabled = useMemo(() => {
        return config.enable.countrySelector || isCookieEnabled('zl_country_selector');
    }, [config]);
    return isEnabled;
};

const CountrySelectorProviderInternal = ({ children }: { children: React.ReactNode }) => {
    const countrySelector = useCountrySelectorShared({
        get,
        put,
    });

    return (
        <countryCodeContext.Provider value={countrySelector}>
            {children}
        </countryCodeContext.Provider>
    );
};

export const CountrySelectorProvider = ({ children }: { children: React.ReactNode }) => {
    const isEnabled = useIsCountrySelectorEnabled();

    return isEnabled ? (
        <CountrySelectorProviderInternal>{children}</CountrySelectorProviderInternal>
    ) : (
        <>{children}</>
    );
};

export const useCountrySelector = () => {
    const isEnabled = useIsCountrySelectorEnabled();
    const context = useContext(countryCodeContext);

    if (!isEnabled) {
        return defaultCountryCodeContext;
    }

    return context;
};

const CountrySelectorInternal = ({ translations }: { translations: Translations }) => {
    const dispatch = useDispatch();
    const { isShown, onSubmit } = useCountrySelector();

    const handleSubmit = useCallback(
        ({ country }) => {
            onSubmit({ country });

            const {
                // @ts-ignore
                countrySelector: { confirmationMessage, confirmationLink },
            } = translations;

            dispatch(
                addSuccessFlashMessage(confirmationMessage, {
                    text: confirmationLink,
                    callback: () => dispatch(removeFlashMessage()),
                }),
            );
        },
        [dispatch, onSubmit],
    );

    // @TODO, to check, when 404 is returned, the dialog is shown, is this expected?

    const CountrySelectorShared = Loadable({
        loader: () =>
            import(
                /* webpackChunkName: "loadable-country-selector" */ './loadable-country-selector'
            ),
        loading() {
            return null;
        },
    });

    return (
        <CountrySelectorShared
            isShown={isShown}
            onSubmit={handleSubmit}
            translations={translations}
        />
    );
};

export const CountrySelector = ({ translations }: { translations: Translations }) => {
    const isEnabled = useIsCountrySelectorEnabled();

    return isEnabled ? <CountrySelectorInternal translations={translations} /> : null;
};
